/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Reznak (reznak@cadwork.cz)
 *
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";

import AONormalsShader from "App/render/webgl/shaders/ssao/ao_normals";
import View from "App/view/view";
import {Framebuffer} from "App/utils/frame_buffer";
import {Containers, Factory} from "App/utils/factory";
import {
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";

/**
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {WebGLRenderGraph} aRenderGraph
 * @class
 * @extends Pass
 */
export default
class AONormals extends Pass {

    public static TypeName: string = "AONormals";

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);
    }


    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): AONormals {
        return new AONormals(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        if (!this._sceneAPI.lightingModule.getSSAO().isEnabled()) {
            return;
        }

        let shaders = this._renderGraph.getLayer('ssao').getShaders();

        this._shadersToBins = {};

        this.attachShaderToBin(OpaqueBinTypeName, shaders.get(AONormalsShader.TypeName));
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(AONormalsShader.TypeName));
    }

    /**
     * Render normals to G-Buffer
     *
     * Possible improvements:
     *  aOutput needs 3 Float values (normal.xyz)
     *
     * @param {View}              aView
     * @param {Framebuffer} aOutput       Float, RGB(A)
     */
    public render(aView: View, aOutput: Framebuffer) {
        /* jshint camelcase:false */
        let gl = this._context.getWebGLRenderingContext();
        let camera = aView.getCamera();
        let scale = aOutput.getParameters().scale;
        let position = aView.getPosition();
        let size = aView.getSize();

        aOutput.bind();
        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // preset uniform to opaque shader
        this.bindShader(OpaqueBinTypeName);

        gl.uniformMatrix3fv(this._activeShaderRenderable.getUniformLocations().u_normal_matrix, false, camera.getViewMatrix3IT());
        gl.uniformMatrix4fv(this._activeShaderRenderable.getUniformLocations().u_view_matrix, false, camera.getViewMatrix());

        // render all the bins related to the geometry pass
        let bins = this._renderGraph.getRenderBins();

        this.view = aView;
        this.state.resetRenderNodesFilters();

        bins.get(OpaqueBinTypeName).render(this);
        bins.get(OpaqueNoTextureBinTypeName).render(this);

        this.unbindShader(); // not necessary here, but to be paired with bindShader above
        this.view = null;
    }
}

Factory.register(Containers.PASSES_SSAO, AONormals);
